/**
 * MODULE: _shared/frontend-shell/src/lib/agenticWorkspaceUrl.ts
 * PURPOSE: Build Traditional → Agentic Workspace deep-link URLs (no import from agentic-shell)
 * DEPENDENCIES: Vite VITE_AGENTIC_WORKSPACE_URL, workspacePeerOrigin, workspaceSessionHandoff
 * PUBLIC_API: getAgenticWorkspaceBaseUrl, buildAgenticMissionUrl, buildAgenticHomeUrl, AgenticLaunchDraft
 * SIDE_EFFECTS: May append #brazn_handoff when a Traditional session exists
 * AGENT SAFETY: 🟢 SAFE
 * FILE_SIZE: ~100 / 150 lines
 * RECENT: 2026-09-15 [2.2.2138] — Hosted same-origin /agent; localhost keeps :5174
 * RECENT_PRIOR: 2026-07-30 [2.2.1844] — Attach session handoff for single-login switch
 */
import {
  resolveWorkspacePeerOrigin,
  type WorkspaceLocationHint,
} from '../../../lib/frontend/api/workspacePeerOrigin';
import { appendWorkspaceSessionHandoff } from '../../../lib/frontend/api/workspaceSessionHandoff';
export type AgenticLaunchDraft = {
  goal?: string;
  entityLabels?: string;
  outputType?: string;
  executionMode?: string;
  approvalPolicy?: string;
  scope?: string;
};

export type AgenticLaunchSource = 'play' | 'signal' | 'suggestion' | 'record' | 'workspace';

export function getAgenticWorkspaceBaseUrl(location?: WorkspaceLocationHint): string {
  return resolveWorkspacePeerOrigin({
    envOverride: import.meta.env.VITE_AGENTIC_WORKSPACE_URL as string | undefined,
    location,
    localFallback: 'http://localhost:5174',
  });
}

export function buildAgenticMissionSearch(
  draft: AgenticLaunchDraft,
  opts?: { from?: AgenticLaunchSource },
): string {
  const p = new URLSearchParams();
  if (draft.goal !== undefined) p.set('goal', draft.goal.trim());
  if (draft.entityLabels?.trim()) p.set('entities', draft.entityLabels.trim());
  if (draft.scope?.trim()) p.set('scope', draft.scope.trim());
  if (draft.outputType) p.set('output', draft.outputType);
  if (draft.executionMode) p.set('mode', draft.executionMode);
  if (draft.approvalPolicy) p.set('approval', draft.approvalPolicy);
  if (opts?.from) p.set('from', opts.from);
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** Full URL to Agentic Home with optional mission composer prefill. */
export function buildAgenticMissionUrl(
  draft: AgenticLaunchDraft,
  opts?: { from?: AgenticLaunchSource },
): string {
  return appendWorkspaceSessionHandoff(
    `${getAgenticWorkspaceBaseUrl()}/agent${buildAgenticMissionSearch(draft, opts)}`,
  );
}

export function buildAgenticHomeUrl(opts?: { from?: AgenticLaunchSource }): string {
  const q = opts?.from ? `?from=${opts.from}` : '';
  return appendWorkspaceSessionHandoff(`${getAgenticWorkspaceBaseUrl()}/agent${q}`);
}
